import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useLocation, useHistory } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  Typography,
  Tab,
  Tabs,
  Hidden,
  Drawer,
  ListItem,
  List,
  ListItemText,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import Divider from '@material-ui/core/Divider';
import { getLogoPath } from '../../logic/logo';

const drawerWidth = 240;
const REACT_APP_GUARDS_URL = process.env.REACT_APP_GUARDS_URL;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  appBar: {
    display: 'flex',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    textTransform: 'lowercase !important',
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
    color: '#fff',
  },
  drawerList: {
    '& i': {
      color: '#fff',
    },
  },
  drawerLink: {
    color: '#fff',
    textTransform: 'lowercase',
    textDecoration: 'none',
    display: 'block',

    '&:hover': {
      textDecoration: 'none',
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
    backgroundColor: '#f5f1ee',
    minHeight: '100vh',

    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(3),
    },
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  bottomBar: {
    top: 'auto',
    bottom: 0,
    borderTop: `1px solid rgba(0, 0, 0, 0.2)`,
    backgroundColor: '#eaeaea',
  },
  tabLink: {
    fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

let timeout = null;

export default (props) => {
  const location = useLocation();
  const history = useHistory();

  const activeTab = ['/app', '/app/meetings/all-events'].includes(location.pathname)
    ? location.pathname
    : false;
  const showFooter =
    location.pathname === '/app' ||
    location.pathname.startsWith('/app/meetings') ||
    location.pathname.startsWith('/app/invitations');
  const bigScreen = useMediaQuery((theme) => theme.breakpoints.up('md'), { noSsr: true });

  const { logout, user } = props;
  const [opened, setOpened] = React.useState(bigScreen);
  const handleOpened = () => setOpened(!opened);

  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const [langMenuAnchorEl, setLangAnchorEl] = React.useState(null);
  const langOpen = Boolean(langMenuAnchorEl);

  React.useEffect(() => {
    navigated();
    history.listen(navigated);
  }, [history, navigated]);

  function navigated() {
    if (timeout) {
      clearTimeout(timeout);
    }

    console.log('reset counter');

    timeout = setTimeout(() => {
      logout(true);
      clearTimeout(timeout);
    }, 1000 * 60 * 60);
  }

  const handleMenu = (setter) => (event) => {
    setter(event.currentTarget);
  };

  const handleClose = (setter) => () => {
    setter(null);
  };

  const handleDrawerClick = () => {
    handleOpened();
  };

  if (bigScreen && !opened) {
    setOpened(true);
  }

  return (
    <div className={classes.root}>
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: opened && bigScreen,
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleOpened}
            edge="start"
            className={clsx(classes.menuButton, opened && classes.hide)}
          >
            <i className="fa fa-bars"></i>
          </IconButton>
          <div style={{ flexGrow: 1 }}>
            <img src={getLogoPath('green-gray')} alt="logo" style={{ maxWidth: '100px' }} />
          </div>
          <div style={{ flexGrow: 1, textAlign: 'right' }}>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu(setLangAnchorEl)}
              color="inherit"
              style={{ fontSize: '1.2rem' }}
            >
              <i className="fa fa-language"></i>&nbsp;{i18n.language.substr(0, 2).toUpperCase()}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={langMenuAnchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={langOpen}
              onClose={handleClose(setLangAnchorEl)}
            >
              <MenuItem onClick={handleClose(setLangAnchorEl)}>
                <a className={classes.menuLink} onClick={() => props.updateLanguage('es')}>
                  Español
                </a>
              </MenuItem>
              <MenuItem onClick={handleClose(setLangAnchorEl)}>
                <a className={classes.menuLink} onClick={() => props.updateLanguage('en')}>
                  English
                </a>
              </MenuItem>
              <MenuItem onClick={handleClose(setLangAnchorEl)}>
                <a className={classes.menuLink} onClick={() => props.updateLanguage('pt')}>
                  Português
                </a>
              </MenuItem>
              <MenuItem onClick={handleClose(setLangAnchorEl)}>
                <a className={classes.menuLink} onClick={() => props.updateLanguage('zh')}>
                  中文
                </a>
              </MenuItem>
              <MenuItem onClick={handleClose(setLangAnchorEl)}>
                <a className={classes.menuLink} onClick={() => props.updateLanguage('fr')}>
                  Français
                </a>
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={opened}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <Typography
            style={{ flexGrow: 1, paddingLeft: '10px', textTransform: 'none' }}
            component="div"
          >
            {user.name} {user.surname}
            <small style={{ display: 'block' }}>{user.company_role}</small>
          </Typography>

          <Hidden mdUp>
            <IconButton onClick={handleOpened} color="inherit">
              <i className="fa fa-chevron-left"></i>
            </IconButton>
          </Hidden>
        </div>
        <Divider />
        <List className={classes.drawerList}>
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app">
                  <i className="fa fa-id-badge fa-fw"></i> {t('My Badge')}
                </Link>
              }
            />
          </ListItem>
          <Divider />
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/invitations">
                  <i className="fa fa-download fa-fw"></i> {t('Received')}
                </Link>
              }
            />
          </ListItem>
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/meetings">
                  <i className="fa fa-upload fa-fw"></i> {t('Sent')}
                </Link>
              }
            />
          </ListItem>
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/meetings/all-events">
                  <i className="fa fa-spinner fa-fw"></i> {t('Pending')}
                </Link>
              }
            />
          </ListItem>
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/meetings/all-events">
                  <i className="fa fa-exchange fa-fw"></i> {t('All')}
                </Link>
              }
            />
          </ListItem>
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/meetings/create">
                  <i className="fa fa-plus-circle fa-fw"></i> {t('New meeting')}
                </Link>
              }
            />
          </ListItem>
          <Divider />
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/account-configuration">
                  <i className="fa fa-cog fa-fw"></i> {t('Account')}
                </Link>
              }
            />
          </ListItem>
          <Divider />
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/buildings">
                  <i className="fa fa-building fa-fw"></i> {t('My buildings')}
                </Link>
              }
            />
          </ListItem>
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/companies">
                  <i className="fa fa-briefcase fa-fw"></i> {t('My companies')}
                </Link>
              }
            />
          </ListItem>
          <Divider />
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <Link className={classes.drawerLink} to="/app/stats">
                  <i className="fa fa-bar-chart fa-fw"></i> {t('My stats')}
                </Link>
              }
            />
          </ListItem>
          {!!user.isSecurity && (
            <>
              <Divider />
              <ListItem button onClick={handleDrawerClick}>
                <ListItemText
                  primary={
                    <a
                      href={`${REACT_APP_GUARDS_URL}/app?token=${localStorage.getItem('token')}`}
                      className={classes.drawerLink}
                    >
                      <i className="fa fa-lock fa-fw"></i> {t('Security staff')}
                    </a>
                  }
                />
              </ListItem>
            </>
          )}
          <Divider />
          <ListItem button onClick={handleDrawerClick}>
            <ListItemText
              primary={
                <span onClick={() => logout()} className={classes.drawerLink}>
                  <i className="fa fa-sign-out fa-fw"></i> {t('Logout')}
                </span>
              }
            />
          </ListItem>
        </List>
      </Drawer>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: opened && bigScreen,
        })}
      >
        {props.children}
      </main>
      <Hidden mdUp>
        {showFooter && (
          <AppBar position="fixed" color="default" className={classes.bottomBar}>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              centered
              variant="fullWidth"
            >
              <Tab
                value={'/app'}
                component={Link}
                className={classes.tabLink}
                to="/app"
                label={
                  <>
                    <i className="fa fa-id-badge fa-fw"></i> {t('My Badge')}
                  </>
                }
              />
              <Tab
                value={'/app/meetings/all-events'}
                component={Link}
                className={classes.tabLink}
                to="/app/meetings/all-events"
                label={
                  <>
                    <i className="fa fa-calendar fa-fw"></i> {t('Meetings')}
                  </>
                }
              />
            </Tabs>
          </AppBar>
        )}
      </Hidden>
    </div>
  );
};
